import { useState } from "react";
import { toast } from "sonner";
import { Phone, MessageCircle, Mail } from "lucide-react";
import { BookingForm } from "@/components/BookingForm";
import { MapBlock } from "@/components/MapBlock";
import { sendContact } from "@/lib/api";
import { SALON } from "@/data/content";

export default function Kontakt() {
  const [form, setForm] = useState({ name: "", contact: "", message: "", consent: false });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Bitte stimmen Sie der Datenverarbeitung zu.");
      return;
    }
    setSending(true);
    try {
      await sendContact({ name: form.name, contact: form.contact, message: form.message });
      toast.success("Vielen Dank! Ihre Nachricht ist eingegangen – wir melden uns schnell zurück.");
      setForm({ name: "", contact: "", message: "", consent: false });
    } catch (err) {
      toast.error(`Das hat nicht geklappt. Rufen Sie uns gerne an: ${SALON.phoneDisplay}.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="page-kontakt">
      <section className="py-16 md:py-20 border-b border-[#24211D]/10">
        <div className="max-w-6xl mx-auto px-6">
          <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">Kontakt & Termin</span>
          <h1 className="text-[clamp(36px,4.5vw,64px)] leading-tight mb-6">Wir freuen uns auf Sie</h1>
          <p className="text-base text-[#24211D]/80 max-w-2xl leading-relaxed">
            Termin anfragen, anrufen oder einfach vorbeikommen: Sie erreichen uns unkompliziert – und dank kurzer Wartezeiten sind Sie schnell dran. Auch kurzfristige Termine sind möglich.
          </p>
        </div>
      </section>

      {/* TERMINANFRAGE */}
      <section id="termin" className="py-16 border-b border-[#24211D]/10 scroll-mt-24">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">Terminvereinbarung</span>
            <h2 className="text-3xl md:text-5xl mb-3">Wunschtermin & Rückruf</h2>
            <p className="text-xs text-[#24211D]/75 font-sans">Senden Sie uns Ihren Wunschzeitraum – wir rufen zur Bestätigung kurz zurück.</p>
          </div>
          <BookingForm />
        </div>
      </section>

      {/* KONTAKTWEGE */}
      <section className="py-16 border-b border-[#24211D]/10 bg-[#F4EFE6]">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl mb-10">Direkte Wege zu uns</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <a href={SALON.phoneHref} data-testid="contact-phone-card" className="border border-[#24211D]/15 bg-[#FFFFFF] p-8 block hover:border-[#B08D57] transition-colors duration-300">
              <Phone className="w-6 h-6 text-[#B08D57] mb-4" />
              <h3 className="text-2xl mb-2">Anrufen</h3>
              <p className="text-xs text-[#24211D]/75 font-sans leading-relaxed mb-3">Der schnellste Weg – auch für kurzfristige Termine am selben Tag.</p>
              <span className="text-sm font-sans font-bold text-[#B08D57]">{SALON.phoneDisplay}</span>
            </a>
            <div className="border border-[#24211D]/15 bg-[#FFFFFF] p-8 opacity-80" data-testid="contact-whatsapp-card">
              <MessageCircle className="w-6 h-6 text-[#B08D57] mb-4" />
              <h3 className="text-2xl mb-2">WhatsApp</h3>
              <p className="text-xs text-[#24211D]/75 font-sans leading-relaxed mb-3">Unser WhatsApp-Kanal ist in Vorbereitung.</p>
              <span className="text-[11px] uppercase tracking-[0.14em] font-sans text-[#24211D]/50">Kommt bald</span>
            </div>
            <a href={SALON.emailHref} data-testid="contact-email-card" className="border border-[#24211D]/15 bg-[#FFFFFF] p-8 block hover:border-[#B08D57] transition-colors duration-300">
              <Mail className="w-6 h-6 text-[#B08D57] mb-4" />
              <h3 className="text-2xl mb-2">E-Mail</h3>
              <p className="text-xs text-[#24211D]/75 font-sans leading-relaxed mb-3">Schreiben Sie uns jederzeit – wir antworten schnell.</p>
              <span className="text-sm font-sans font-bold text-[#B08D57] break-all">nezahatozan62@gmail.com</span>
            </a>
          </div>
        </div>
      </section>

      {/* KONTAKTFORMULAR + ANFAHRT */}
      <section className="py-16 border-b border-[#24211D]/10">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="text-3xl md:text-4xl mb-6">Nachricht schreiben</h2>
            <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
              <div>
                <label htmlFor="contact-name" className="nes-label">Name *</label>
                <input id="contact-name" data-testid="contact-name-input" type="text" required className="nes-input" placeholder="Ihr Name" value={form.name} onChange={set("name")} />
              </div>
              <div>
                <label htmlFor="contact-channel" className="nes-label">Telefonnummer *</label>
                <input id="contact-channel" data-testid="contact-channel-input" type="text" required className="nes-input" placeholder="Womit können wir Sie erreichen?" value={form.contact} onChange={set("contact")} />
              </div>
              <div>
                <label htmlFor="contact-message" className="nes-label">Ihre Nachricht *</label>
                <textarea id="contact-message" data-testid="contact-message-input" rows={4} required className="nes-input resize-none" placeholder="Worum geht es?" value={form.message} onChange={set("message")} />
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" data-testid="contact-consent-checkbox" checked={form.consent} onChange={set("consent")} className="mt-1 accent-[#B08D57]" />
                <span className="text-[11px] text-[#24211D]/70 leading-relaxed font-sans">
                  Ich stimme zu, dass meine Angaben zur Beantwortung der Anfrage verarbeitet werden. Details in der Datenschutzerklärung. *
                </span>
              </label>
              <button type="submit" disabled={sending} data-testid="contact-form-submit-button" className="bg-[#B08D57] hover:bg-[#967442] disabled:opacity-60 text-[#FBF8F3] px-10 py-4 text-xs uppercase tracking-[0.2em] font-sans transition-colors duration-300">
                {sending ? "Wird gesendet ..." : "Nachricht absenden"}
              </button>
            </form>
          </div>
          <div>
            <h2 className="text-3xl md:text-4xl mb-6">Anfahrt & Parken</h2>
            <p className="text-sm text-[#24211D]/80 leading-relaxed mb-3" data-testid="contact-address">
              {SALON.name}<br />{SALON.street}<br />{SALON.city}
            </p>
            <p className="text-xs text-[#24211D]/70 font-sans leading-relaxed mb-6" data-testid="contact-parking">
              Das Studio liegt zentral in Glessen. Parkmöglichkeiten finden Sie direkt in der Nähe des Heidenpfuhls – genaue Details zur Anfahrt ergänzen wir hier in Kürze.
            </p>
            <MapBlock />
          </div>
        </div>
      </section>
    </div>
  );
}
